import { useLazyQuery } from '@apollo/client';
import { pdf } from '@react-pdf/renderer';
import { message } from 'antd';
import React from 'react';
import { StaffGenerateVerificationFormButtonContainerCredentialVerificationCaseV1FileByIdDocument } from '../../../../../../generated';
import { AuthResult, AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';
import GeneratePdfFormButton from '../../../../../shared/pdf-generation/generate-pdf-form-button';
import PdfVerificationSignaturePage from '../../../../../shared/pdf-generation/pdf-pages/pdf-verification-signature-page';


interface GenerateVerificationFormButtonContainerProps {
  authorizeRequest: (file: File) => Promise<AuthResult>;
  onSuccess: (response: AzureBlobResponse) => Promise<void>;
  isProcessing: boolean;
  onStartPdfGeneration: () => void;
  isProcessingUploadingFile?: boolean;
  caseId: string;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const GenerateVerificationFormButtonContainer: React.FC<GenerateVerificationFormButtonContainerProps> = (props) => {
  const onSuccess = async (response: AzureBlobResponse) => {
    await props.onSuccess(response);
    message.success('Verification Form generated successfully');
    props.setIsEditing && props.setIsEditing(false);
  };

  const [getPdfData, { loading: caseLoading }] = useLazyQuery(StaffGenerateVerificationFormButtonContainerCredentialVerificationCaseV1FileByIdDocument, {
    variables: {
      credentialVerificationCaseV1Id: props.caseId
    },
    fetchPolicy: 'network-only'
  });

  const getGenerateIdFormData = async () => {
    props.onStartPdfGeneration();
    const pdfData = await getPdfData();
    const caseData = pdfData.data?.credentialVerificationCaseV1;
    const combinedData = {
      dateOfBirth: caseData?.applicant?.personalInformation?.identityDetails?.dateOfBirth,
      gender: caseData?.applicant?.personalInformation?.identityDetails?.gender,
      applicantName: caseData?.applicant?.displayName,
      nameOnCredential: caseData?.caseDetails?.application?.nameOnCredential,
      entityId: caseData?.caseDetails?.application?.issuingInstitution?.id || 'Other',
      caseId: caseData?.id,
      applicantId: caseData?.applicant?.id
    };

    return { combinedData };
  };

  interface IdFormData {  
    dateOfBirth: string;  
    gender: string;  
    applicantName: string;  
    entityId: string;
    caseId: string;  
    applicantId: string;  
    nameOnCredential: string;  
  }  

  const generateIdFormPdf = async (data: IdFormData): Promise<File> => {
    if (!data.dateOfBirth || !data.gender || !data.applicantName || !data.caseId || !data.applicantId || !data.nameOnCredential || !data.entityId) {
      throw new Error('Missing data to generate verification form');
    }

    const pdfBlob = await pdf(<PdfVerificationSignaturePage data={data} />).toBlob();
    return new File([pdfBlob], 'verification-form.pdf', { type: 'application/pdf' });
  };

  return (
    <GeneratePdfFormButton
      isProcessingUploadingFile={props.isProcessingUploadingFile}
      authorizeRequest={props.authorizeRequest}
      onSuccess={onSuccess}
      generateFormPdf={generateIdFormPdf}
      generateFormPdfData={getGenerateIdFormData}
      isLoading={props.isProcessing || caseLoading}
    />
  );
};

